<template>
  <b-card class="login-card">
    <section>
      <header class="text-align-center">
        <img class="brand" src="@/assets/logo.png" />
      </header>
      <div class="body">
        <h2>Welcome.</h2>
        <p class="text-muted auth-message">Create a Leaf account</p>
        <form class="no-margin" @submit.prevent="registerUser">
          <fieldset>

            <div class="form-group">
              <div class="input-group input-group-md">
                <div class="input-group-prepend">
                  <span class="input-group-text"><i class="fas fa-user"></i></span>
                </div>
                <input name="firstName" type="text" class="form-control input-lg" v-model.trim.lazy="firstName" v-validate="'required'" :class="{'is-invalid': errors.has('firstName') }" placeholder="First Name">
              </div>
              <b-form-text class="is-invalid help" v-show="errors.has('firstName')">
                {{ errors.first('firstName') }}
              </b-form-text>
            </div>

            <div class="form-group">
              <div class="input-group input-group-md">
                <div class="input-group-prepend">
                  <span class="input-group-text"><i class="fas fa-address-card"></i></span>
                </div>
                <input name="lastName" type="text" class="form-control input-lg" v-model.trim.lazy="lastName" v-validate="'required'" :class="{'is-invalid': errors.has('lastName') }" placeholder="Last Name">
              </div>
              <b-form-text class="is-invalid help" v-show="errors.has('lastName')">
                {{ errors.first('lastName') }}
              </b-form-text>
            </div>

            <div class="form-group">
              <div class="input-group input-group-md">
                <div class="input-group-prepend">
                  <span class="input-group-text"><i class="fas fa-at"></i></span>
                </div>
                <input name="email" type="email" class="form-control input-lg" v-model.trim.lazy="email" v-validate="'required|email'" :class="{'is-invalid': errors.has('email') }" placeholder="Email">
              </div>
              <b-form-text class="is-invalid help" v-show="errors.has('email')">
                {{ errors.first('email') }}
              </b-form-text>
            </div>

            <div class="form-group">
              <div class="input-group input-group-md">
                <div class="input-group-prepend">
                  <span class="input-group-text"><i class="fa fa-lock"></i></span>
                </div>
                <input name="password" :type="passwordInputType" class="form-control input-lg" v-model.trim="password" v-validate="'required|password'" :class="{'is-invalid': errors.has('password') }" placeholder="Password">
                <div class="input-group-append">
                  <span class="input-group-text"
                    @mousedown="showPassword"
                    @touchstart="showPassword"
                    @mouseleave="hidePassword"
                    @mouseup="hidePassword"
                    @touchend="hidePassword"
                    @touchcancel="hidePassword"
                  ><i class="far" :class="viewPasswordIcon"></i></span>
                </div>
              </div>
              <b-form-text class="is-invalid help" v-show="errors.has('password')" v-html="errors.first('password')"></b-form-text>
            </div>

            <div class="form-group">
              <div class="form-check">
                <input name="termsAgree" type="checkbox" class="form-check-input" v-model="termsAgree" v-validate="'required'" :class="{'is-invalid': errors.has('termsAgree') }">
                <label class="form-check-label">Agree to Terms & Conditions</label>
                <b-form-text class="is-invalid help" v-show="errors.has('termsAgree')" v-html="errors.first('termsAgree')"></b-form-text>
              </div>
            </div>

          </fieldset>
          <div class="form-actions">
            <button type="submit" class="btn btn-success btn-lg btn-block">
              <span class="" v-if="loading">
                <i class="fa fa-spinner fa-spin"></i>
              </span>
              <small> Create Account</small>
            </button>
            <div class="forgot">
              Already have an account? <router-link :to="{name: 'Login'}">Login</router-link>
              <br/>
              <!--<router-link :to="{name: 'Register'}" class="forgot">Forgot Password?</router-link>-->
            </div>
          </div>
        </form>
      </div>
    </section>
  </b-card>
</template>

<script>
import { SIGNUP_MUTATION } from '@/constants/graphql'
import { Validator } from 'vee-validate'
import { saveSession, isLoggedIn } from '@/lib/auth'

const messages = {
  en: {
    messages: {
      required: () => 'This field is required.'
    }
  }
}

Validator.localize(messages)

Validator.extend('password', {
  getMessage: () => {
    return 'Passwords have no special restrictions other than length (minimum 8 characters), so go for a long passphrase that you can remember. For example, the phrase ' +
      '"How now brown cow?" is much better than "P@ssw0rd". <a target="_blank" href="https://howsecureismypassword.net/">https://howsecureismypassword.net</a>'
  },
  validate: (value) => {
    return value.length >= 8
  }
})

export default {
  name: 'Register',
  data: () => ({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    termsAgree: false,
    loading: 0,
    showPasswordInterval: false,
    passwordInputType: 'password',
    returnUrl: '/'
  }),
  computed: {
    viewPasswordIcon () {
      return (this.passwordInputType === 'password') ? 'fa-eye-slash' : 'fa-eye'
    }
  },
  methods: {
    showPassword () {
      if (!this.showPasswordInterval) {
        this.showPasswordInterval = setInterval(() => {
          this.passwordInputType = 'text'
        }, 30)
      }
    },
    hidePassword () {
      clearInterval(this.showPasswordInterval)
      this.showPasswordInterval = false
      this.passwordInputType = 'password'
    },
    registerUser () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.loading = true
          const formData = {
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password
          }

          this.$apollo.mutate({
            mutation: SIGNUP_MUTATION,
            variables: formData
          }).then(() => {
            saveSession(result.data.signup)
            this.loading = false
            this.$swal.fire({ icon: 'success', title: 'Success! Registered & Logged In' })
            this.$router.push(this.returnUrl)
          }).catch((error) => {
            this.loading = false
            this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: ' + error })
          })
        } else {
          alert('Please correct errors before submitting.')
        }
      })
    }
  },
  beforeCreate () {
    if (isLoggedIn()) {
      this.$router.push({ 'name': 'Dashboard' })
    }
    document.body.classList.add('photo-bg')
  }
}
</script>

<style scoped>
  .termsAgree {
    margin-bottom: 15px;
  }
  .brand {
    width: 100px;
  }
</style>
